import React, { useEffect, useState } from 'react';
import { bulkAssignAnnualReview, getApprovers } from '../api/endpoints';
import { useToast } from './Toast';
import type { BulkAssignReviewResponse, ConstraintSetList, User } from '../types';

interface Props {
  /** Sets the user already selected on the dashboard. */
  selectedSets: ConstraintSetList[];
  onClose: () => void;
  /** Called after a successful assign with the server response. The
   *  dashboard uses this to clear the selection + refresh the
   *  assignments widget. */
  onAssigned: (result: BulkAssignReviewResponse) => void;
}

/** Modal launched from DashboardPage when N constraint sets are checked.
 *  Pick a reviewer from the existing /approvers list (any user with
 *  can_approve — the backend rejects non-approvers anyway), optionally
 *  add a note, click Assign. v0.6.0.12. */
export default function AssignReviewModal({
  selectedSets, onClose, onAssigned,
}: Props) {
  const { showToast } = useToast();
  const [approvers, setApprovers] = useState<User[]>([]);
  const [loadingApprovers, setLoadingApprovers] = useState(true);
  const [reviewerId, setReviewerId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // v0.6.0.14: when toggled, fetch ALL approvers regardless of body site.
  // Otherwise (default), filter approvers to the union of the selected
  // sets' body sites via User.approves_body_sites M2M.
  const [showAllApprovers, setShowAllApprovers] = useState(false);

  // Compute the union of body sites from the selected sets as a STRING
  // key (e.g., "5,7,9"). useMemo on the object/array form would still
  // produce a new array reference each render because `selectedSets`
  // changes identity on every parent render (DashboardPage's
  // `constraintSets.filter(...)` produces a fresh array). The useEffect
  // dep would never compare equal → refetch fires every parent render.
  // A primitive string key compares by value and dedupes correctly.
  // Adversarial review v0.6.0.14 cross-model finding.
  const selectedBodySiteIdsKey = React.useMemo(() => {
    const ids = new Set<number>();
    for (const s of selectedSets) {
      if (typeof s.body_site === 'number') ids.add(s.body_site);
    }
    return Array.from(ids).sort((a, b) => a - b).join(',');
  }, [selectedSets]);

  useEffect(() => {
    setLoadingApprovers(true);
    // Pass the body-site union when filtering is enabled. Empty union
    // falls through to the unfiltered list (safer than returning []).
    const ids = selectedBodySiteIdsKey
      ? selectedBodySiteIdsKey.split(',').map(Number)
      : [];
    const bodySiteArg =
      showAllApprovers || ids.length === 0 ? undefined : ids;

    // AbortController guards against the stale-response race: rapid
    // toggles of showAllApprovers (or rapid changes of the body-site
    // union) could land an older response after a newer one and
    // overwrite the visible filter state. cancelled flag is a
    // belt-and-suspenders in case axios doesn't honor the signal.
    // Codex finding v0.6.0.14.
    let cancelled = false;
    getApprovers(bodySiteArg)
      .then((res) => {
        if (cancelled) return;
        setApprovers(res.data);
        if (res.data.length > 0) {
          setReviewerId((prev) =>
            prev !== null && res.data.some((a) => a.id === prev)
              ? prev
              : res.data[0].id,
          );
        } else {
          setReviewerId(null);
        }
      })
      .catch(() => {
        if (cancelled) return;
        showToast('Failed to load reviewers.', 'error');
      })
      .finally(() => {
        if (!cancelled) setLoadingApprovers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showToast, showAllApprovers, selectedBodySiteIdsKey]);

  // Derived (for UI counters in the label): re-derive ids from the key
  // when we need to show the count to the user. Cheap split, not in a
  // useEffect dep — pure render-time computation.
  const selectedBodySiteCount = selectedBodySiteIdsKey
    ? selectedBodySiteIdsKey.split(',').length
    : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (reviewerId === null) {
      showToast('Pick a reviewer first.', 'warning');
      return;
    }
    if (selectedSets.length === 0) {
      showToast('No sets selected.', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      const res = await bulkAssignAnnualReview({
        set_ids: selectedSets.map((s) => s.id),
        assigned_to: reviewerId,
        notes: notes.trim() || undefined,
      });
      const { created, skipped_existing } = res.data;
      const reviewerName = approvers.find((a) => a.id === reviewerId)?.username ?? 'reviewer';
      const parts = [`Assigned ${created} set${created === 1 ? '' : 's'} to ${reviewerName}.`];
      if (skipped_existing > 0) {
        parts.push(`${skipped_existing} already had a pending assignment.`);
      }
      showToast(parts.join(' '), 'success');
      onAssigned(res.data);
    } catch (err: any) {
      const data = err?.response?.data;
      let detail = 'Failed to assign review.';
      if (typeof data === 'string') {
        detail = data;
      } else if (data?.error) {
        detail = String(data.error);
      } else if (data?.detail) {
        detail = String(data.detail);
      }
      showToast(detail, 'error');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="assign-review-title"
        className="modal-content"
        style={{ maxWidth: 520 }}
      >
        <div
          className="modal-header"
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--color-border)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}
        >
          <h2 id="assign-review-title" style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
            Assign annual review
          </h2>
          <button type="button" className="btn-sm" onClick={onClose} aria-label="Close">×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: 20 }}>
          <div style={{ marginBottom: 16 }}>
            <strong>{selectedSets.length} set{selectedSets.length === 1 ? '' : 's'} selected:</strong>
            <ul style={{
              maxHeight: 120, overflowY: 'auto', margin: '8px 0 0', paddingLeft: 20,
              fontSize: 13, color: 'var(--color-text-secondary)',
            }}>
              {selectedSets.map((s) => (
                <li key={s.id}>{s.body_site_name} — {s.name} ({s.fractions} fx)</li>
              ))}
            </ul>
          </div>

          <div className="form-group">
            <label htmlFor="assign-review-reviewer">
              Reviewer
              {/* Show the filter state so the admin knows why a reviewer
                  they expected isn't in the list. v0.6.0.14. */}
              {!showAllApprovers && selectedBodySiteCount > 0 && (
                <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: 8, fontSize: 12 }}>
                  (filtered to reviewers assigned to {selectedBodySiteCount === 1 ? 'this body site' : `${selectedBodySiteCount} body sites`})
                </span>
              )}
            </label>
            {loadingApprovers ? (
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                Loading reviewers…
              </div>
            ) : approvers.length === 0 ? (
              <div style={{ fontSize: 13 }}>
                {!showAllApprovers && selectedBodySiteCount > 0 ? (
                  <>
                    <div style={{ color: 'var(--color-warning, #b45309)', marginBottom: 8 }}>
                      No approver is assigned to {selectedBodySiteCount === 1 ? 'this body site' : 'any of these body sites'}.
                    </div>
                    <button
                      type="button"
                      className="btn-sm"
                      onClick={() => setShowAllApprovers(true)}
                    >
                      Show all approvers
                    </button>
                  </>
                ) : (
                  <div style={{ color: 'var(--color-danger)' }}>
                    No approvers available. An admin must mark a user as approver before annual
                    reviews can be assigned.
                  </div>
                )}
              </div>
            ) : (
              <>
                <select
                  id="assign-review-reviewer"
                  value={reviewerId ?? ''}
                  onChange={(e) => setReviewerId(Number(e.target.value))}
                  required
                >
                  {approvers.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.username}
                      {a.first_name || a.last_name ? ` (${a.first_name} ${a.last_name})`.trim() : ''}
                    </option>
                  ))}
                </select>
                {selectedBodySiteCount > 0 && (
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    marginTop: 6, fontSize: 12, cursor: 'pointer',
                    color: 'var(--color-text-secondary)',
                  }}>
                    <input
                      type="checkbox"
                      checked={showAllApprovers}
                      onChange={(e) => setShowAllApprovers(e.target.checked)}
                    />
                    Show all approvers (not just those assigned to these body sites)
                  </label>
                )}
              </>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="assign-review-notes">Note to reviewer (optional)</label>
            <textarea
              id="assign-review-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="e.g., Q2 cycle — please confirm IMRT compatibility."
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || loadingApprovers || approvers.length === 0 || reviewerId === null}
            >
              {submitting ? 'Assigning…' : `Assign ${selectedSets.length} set${selectedSets.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
